/**
 * Town & Country — let the sticky add-to-cart bar run, and lift the Shopify
 * Inbox launcher clear of it.
 *
 * WHY THIS EXISTS
 * ---------------
 * Horizon refuses to show its sticky add-to-cart bar whenever a chat widget is
 * on the page — `#isChatActive()` in assets/sticky-add-to-cart.js gates every
 * call to `#showStickyBar()`. That default is sound: on mobile the bar is
 * full-width at bottom:0 (74px) and the Inbox launcher sits at bottom:20px
 * (46px), so measured on this store they overlap by 46px, with the bubble
 * landing on the Add to cart button.
 *
 * We want both. So the launcher moves up out of the way, and the gate is
 * satisfied so the bar can render.
 *
 * WHY THIS NEEDS JAVASCRIPT AT ALL
 * --------------------------------
 * Horizon's gate is `document.querySelector('shopify-chat')` — an EXISTENCE
 * check, not a visibility one. Verified: hiding the launcher outright still
 * left the bar suppressed. `document.querySelector` does not pierce shadow
 * DOM, so moving <shopify-chat> into a shadow root of our own makes the gate
 * return false while the element stays in the page and renders normally.
 *
 * That is the whole of the JavaScript: wait for Inbox, move it once, add a
 * stylesheet. There are deliberately NO MutationObservers here.
 *
 * A previous attempt (reverted in ca8ba25) put a chat button in the header and
 * kept it alive with a MutationObserver on document.body. That observer fired
 * on its own mutations, and because Horizon's morph.js strips any element not
 * present in the server HTML, it re-inserted the button on every morph — which
 * hung the preview and crashed browsers. Nothing here watches the DOM. If this
 * file ever needs to react to something, do it on Horizon's section-render
 * events, not by observing document.
 */
(function () {
  'use strict';

  /* Launcher sits at bottom:20px; the mobile bar is 74px tall. Shortening the
     chat host lifts its bottom-anchored contents by the same amount, so 62px
     puts the launcher at bottom:82px — 8px clear of the bar. */
  var LIFT_PX = 62;
  var HOST_ID = 'tc-chat-host';
  var POLL_MS = 250;
  var TIMEOUT_MS = 20000;

  function apply(chat) {
    if (document.getElementById(HOST_ID)) return;

    var host = document.createElement('div');
    host.id = HOST_ID;
    document.body.appendChild(host);

    var root = host.attachShadow({ mode: 'open' });

    /* Lives inside our shadow root because that is where <shopify-chat> now
       is — a stylesheet in the document could no longer reach it. Applied
       unconditionally rather than only while the bar is showing: watching the
       bar would mean observing the DOM, which is the thing that went wrong
       last time, and 82px is unremarkable on pages without a sticky bar. */
    var style = document.createElement('style');
    style.textContent = 'shopify-chat { height: calc(100% - ' + LIFT_PX + 'px) !important; }';
    root.appendChild(style);

    root.appendChild(chat);
  }

  function init() {
    var chat = document.querySelector('shopify-chat');
    if (chat) {
      apply(chat);
      return;
    }

    // Inbox injects <shopify-chat> asynchronously. Poll rather than observe —
    // bounded, cheap, and it cannot be re-entered by its own side effects.
    var waited = 0;
    var timer = setInterval(function () {
      var found = document.querySelector('shopify-chat');
      waited += POLL_MS;

      if (found) {
        clearInterval(timer);
        apply(found);
      } else if (waited >= TIMEOUT_MS) {
        // Inbox isn't installed, or its embed is off. Nothing to do.
        clearInterval(timer);
      }
    }, POLL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
